﻿/* eslint no-use-before-define: 0 */
import { saveAs } from 'file-saver';

import * as Sentry from '@sentry/react';

import axios from 'config/axios';
import { userTypeIds } from 'config/config';
import { getErrorMessage } from 'helpers/errorHandling';

import history from 'global-history';

import { toast } from 'react-toastify';

/**
 * The diet recommendations are built from the STAGE CONFIG rather than from the stage's
 * own regimens, and EVERY link in that chain can be absent: the config for this stage
 * index, a regimen named exactly 'DIET RECOMMENDATIONS', a step's options array, and the
 * option's item. Unguarded, this ran while BUILDING the page data, so one missing name
 * threw and took down the whole regimen page - a red box where a client's regimen should
 * be, not a missing diet section.
 *
 * Nothing prevented that except that all eight stage configs happen to carry that exact
 * name, which no code enforces; and `GET programs/acne/stages/configs/` is already known
 * to return null options elsewhere, which is the CreateRegimen crash recorded in
 * CLAUDE.md. So this returns [] instead of throwing, and a step whose option or item is
 * missing is dropped rather than rendered as a blank row.
 *
 * Exported for its spec: this is the whole point of the guard and needs a test that
 * fails without it.
 */
export const buildDietSteps = (stageConfig) =>
  ((stageConfig?.regimens ?? []).find((regimen) => regimen.name === 'DIET RECOMMENDATIONS')?.steps ?? [])
    .map((step) => {
      const option = step?.options?.[0];
      if (!option?.item?.name) return null;
      return {
        instructions: option.instructions,
        text: option.item.name,
        regimen_id: 'diet-recommendations',
      };
    })
    .filter(Boolean);

// Actions
const INITIALIZE = 'cc/viewRegimen/INITIALIZE';
const INITIALIZE_SUCCESS = 'cc/viewRegimen/INITIALIZE_SUCCESS';
const INITIALIZE_FAIL = 'cc/viewRegimen/INITIALIZE_FAIL';
const DOWNLOAD = 'cc/viewRegimen/DOWNLOAD';
const DOWNLOAD_SUCCESS = 'cc/viewRegimen/DOWNLOAD_SUCCESS';
const DOWNLOAD_FAIL = 'cc/viewRegimen/DOWNLOAD_FAIL';

const initialState = {
  loading: false,
  stage: {},
  regimens: [],
};

// Reducer
const reducer = (state = initialState, action = {}) => {
  switch (action.type) {
    case INITIALIZE:
      return {
        ...state,
        loading: true,
      };
    case INITIALIZE_SUCCESS: {
      const { stage, regimens } = action.payload;
      return {
        ...state,
        stage,
        regimens,
        loading: false,
      };
    }
    default:
      return state;
  }
};

// Action Creators

const initialize = (payload) => async (dispatch, getState) => {
  dispatch({ type: INITIALIZE, payload });
  const { stageId, branch } = payload;
  const { jwt, id: userId, userType } = getState().user;
  const { stageConfigs } = getState().app;

  const headers = {
    headers: {
      Authorization: `Bearer ${jwt}`,
      'Content-Type': 'application/json',
    },
  };

  const isClient = userType?.id === userTypeIds.client;

  try {
    if (isClient) {
      const { data: acneProgramData } = await axios.get(
        `programs/acne/?user=${userId}`,
        headers,
      );
      const acneProgram = acneProgramData?.results[0];

      if (!acneProgram) {
        history.push('/');
        return;
      }

      if (!acneProgram.consented) {
        history.push(`/regimen-consent/${acneProgram.id}`);
        return;
      }
    }

    const { data: stage } = await axios.get(`stages/${stageId}/`, headers);

    if (stage?.acne_program === '') {
      // eslint-disable-line camelcase
      history.push('/');
      return;
    }

    const { regimens } = stage;
    let stageIndex = stage.program_view_index;
    if (stage.program_view_index > branch.program_config.duration) {
      stageIndex = branch.program_config.duration;
    }

    const stageConfig = stageConfigs[stageIndex];
    const dietSteps = buildDietSteps(stageConfig);
    const configuredDietSteps = (stageConfig?.regimens ?? [])
      .find((regimen) => regimen.name === 'DIET RECOMMENDATIONS')?.steps ?? [];

    // Degrade quietly for the client, but NOT silently for us. A guard that hides a data
    // fault is how such a fault goes unnoticed for months, so the absence is reported.
    if (dietSteps.length !== configuredDietSteps.length || configuredDietSteps.length === 0) {
      Sentry.captureMessage('ViewRegimen: diet recommendations config missing or incomplete', {
        level: 'warning',
        extra: {
          stageId: stage?.id,
          stageIndex,
          hasStageConfig: Boolean(stageConfig),
          configuredSteps: configuredDietSteps.length,
          usableSteps: dietSteps.length,
        },
      });
    }

    // group regimen by treatment area, herbal, or diet recommendations, etc
    const groupedRegimens = [
      {
        code: 'HERBAL',
        label: 'Your Herbal Regimen',
        regimens: regimens.filter(({ code }) => code.includes('HERBAL')),
      },
      {
        code: 'FACE',
        label: 'Your Face Product Regimen',
        regimens: regimens
          .filter(({ code }) => code.includes('FACE'))
          .sort((a, b) => {
            if (a.code < b.code) {
              return 1;
            }
            if (b.code < a.code) {
              return -1;
            }
            return 0;
          }),
      },
      {
        code: 'CHEST',
        label: 'Your Chest Product Regimen',
        regimens: regimens
          .filter(({ code }) => code.includes('CHEST'))
          .sort((a, b) => {
            if (a.code < b.code) {
              return 1;
            }
            if (b.code < a.code) {
              return -1;
            }
            return 0;
          }),
      },
      {
        code: 'BACK',
        label: 'Your Back Product Regimen',
        regimens: regimens
          .filter(({ code }) => code.includes('BACK'))
          .sort((a, b) => {
            if (a.code < b.code) {
              return 1;
            }
            if (b.code < a.code) {
              return -1;
            }
            return 0;
          }),
      },
      // Omitted entirely when there is nothing usable, rather than included as a heading
      // with no rows underneath it.
      ...(dietSteps.length
        ? [{
          code: 'DIET',
          label: 'Your Diet Recommendations',
          regimens: [
            {
              code: 'DIET RECOMMENDATIONS',
              id: 'diet-recommendations',
              steps: dietSteps,
            },
          ],
        }]
        : []),
    ];

    dispatch(initializeSuccess({ stage, regimens: groupedRegimens }));
  } catch (error) {
    dispatch(initializeFail({ error }));
  }
};

const initializeSuccess = (payload) => (dispatch) => {
  dispatch({ type: INITIALIZE_SUCCESS, payload });
};

const initializeFail = (payload) => (dispatch) => {
  dispatch({ type: INITIALIZE_FAIL, payload });
  const { error } = payload;
  toast.error(getErrorMessage(error, 'Could not initialize View Regimen'));
};

const download = (payload) => async (dispatch, getState) => {
  dispatch({ type: DOWNLOAD, payload });
  const { stage } = getState().viewRegimen;
  const { jwt } = getState().user;

  const options = {
    headers: {
      Authorization: `Bearer ${jwt}`,
    },
    responseType: 'blob',
  };

  try {
    const { data: pdf } = await axios.get(
      `stages/${stage.id}/download/`,
      options,
    );
    saveAs(pdf, 'Clear_Connection_Regimen');
  } catch (error) {
    dispatch(downloadFail({ error }));
  }
};

const downloadSuccess = (payload) => (dispatch) => {
  dispatch({ type: DOWNLOAD_SUCCESS, payload });
};

const downloadFail = (payload) => (dispatch) => {
  dispatch({ type: DOWNLOAD_FAIL, payload });
};

export default reducer;
export {
  // Actions
  INITIALIZE,
  INITIALIZE_SUCCESS,
  INITIALIZE_FAIL,
  DOWNLOAD,
  DOWNLOAD_SUCCESS,
  DOWNLOAD_FAIL,
  // Action Creators
  initialize,
  initializeFail,
  initializeSuccess,
  download,
  downloadFail,
  downloadSuccess,
};
